import {
  Typography,
  Button,
  Box,
  Paper,
  IconButton,
  Skeleton,
} from '@mui/material';
import React, { useState } from 'react';
import { Plus, PencilSimple, TrashSimple } from '@phosphor-icons/react';
import { useDashboard } from '../../../hooks/useDashboard';
import { useDispatch } from 'react-redux';
import {
  resetWebForm,
  setFunctionName,
  setFunctionDescription,
  setSubmitButtonText,
  setSuccessMessage,
  setFormDescription,
  setFormTitle,
  addFormField,
  addParameter,
} from '../../../utils/redux/slices/webformSlice';
import { deleteWebFormApi } from '../../../utils/apis';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAlert } from '../../../hooks/useAlert';
import DeleteModal from '../../modals/DeleteModal';
import MessagePopup from '../../common/MessagePopup';
import { useAgents } from '../../../hooks/useAgents';
import WebFormFunctionDrawer from './WebFormFunctionDrawer';

const WebForm: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const { isLoading } = useAgents();
  const { selectedAssistant } = useDashboard();
  const webforms = selectedAssistant?.functionconfig?.webform || [];
  const [editingWebform, setEditingWebform] = React.useState<any>(null);
  const dispatch = useDispatch();

  // New state for delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [webformToDelete, setWebformToDelete] = useState<any>(null);
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();

  // Delete webform mutation
  const { mutate: deleteWebform, isPending: isDeletePending } = useMutation({
    mutationFn: (webform: any) => {
      const functionName = webform.name || webform.function_name;
      if (!functionName) {
        console.error('Function name is undefined', webform);
        throw new Error('Function name is undefined');
      }
      return deleteWebFormApi(selectedAssistant?._id || '', functionName);
    },
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      showAlert('Webform function deleted successfully', 'success');
    },
    onError: error => {
      console.error('Error deleting webform:', error);
      showAlert('Error deleting webform function', 'error');
    },
  });

  const handleEditWebform = (webform: any) => {
    dispatch(resetWebForm());

    const latestWebforms = selectedAssistant?.functionconfig?.webform || [];

    const latestWebform =
      latestWebforms.find((w: any) => w.name === webform.name) || webform;

    setEditingWebform(latestWebform);

    dispatch(setFunctionName(latestWebform.name || ''));
    dispatch(setFunctionDescription(latestWebform.description || ''));

    const buttonText =
      latestWebform.webform_config?.submit_button_text || 'Submit';
    dispatch(setSubmitButtonText(buttonText));

    const successMessage =
      latestWebform.webform_config?.success_message ||
      'Form submitted successfully!';
    dispatch(setSuccessMessage(successMessage));

    const formDescription =
      latestWebform.webform_config?.description ||
      latestWebform.form_description ||
      '';
    dispatch(setFormDescription(formDescription));

    const formTitle =
      latestWebform.display_name || latestWebform.webform_config?.title || '';
    dispatch(setFormTitle(formTitle));

    // Handle form fields - IMPORTANT: clear existing fields first if needed
    const fieldsArray =
      latestWebform.fields || latestWebform.webform_config?.fields || [];
    if (Array.isArray(fieldsArray)) {
      fieldsArray.forEach((field: any) => {
        dispatch(
          addFormField({
            name: field.name,
            label: field.label,
            type: field.type,
            required: field.required || false,
            placeholder: field.placeholder || '',
            options: field.options
              ? field.options.map((opt: any) => opt.value || opt.label)
              : [],
          })
        );
      });
    }

    // Handle parameters from webformData if they exist
    if (latestWebform.parameters && latestWebform.parameters.properties) {
      const requiredParams = latestWebform.parameters.required || [];
      Object.entries(latestWebform.parameters.properties).forEach(
        ([paramName, paramConfig]: [string, any]) => {
          dispatch(
            addParameter({
              name: paramName,
              type: paramConfig.type || 'string',
              description: paramConfig.description || '',
              required: requiredParams.includes(paramName),
            })
          );
        }
      );
    }

    // 5. Now open the drawer with populated data
    setOpen(true);
  };

  const handleDeleteWebform = (webform: any) => {
    setWebformToDelete(webform);
    setIsDeleteModalOpen(true);
  };

  // Handle actual deletion
  const handleConfirmDelete = () => {
    if (webformToDelete) {
      deleteWebform(webformToDelete);
    }
  };

  const renderWebhookList = () => {
    return (
      <Box
        width="100%"
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        {webforms.map((webform: any, index: number) => (
          <Paper
            key={webform.name || webform.function_name || index}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              padding: '16px',
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                <Typography sx={{ fontWeight: 500 }}>
                  {webform.function_name || webform.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {webform.function_description || webform.description}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <IconButton
                  onClick={() => handleEditWebform(webform)}
                  size="small"
                >
                  <PencilSimple size={18} />
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteWebform(webform)}
                  size="small"
                >
                  <TrashSimple size={18} weight="fill" color="#E74C3C" />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderEmptyState = () => {
    return (
      <>
        <Typography
          sx={{ fontSize: '14px', fontWeight: '400', color: '#6B6F80' }}
        >
          No Webform Functions created yet. Create your first function to get
          started
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Plus size={16} />}
          onClick={() => {
            dispatch(resetWebForm());
            setEditingWebform(null);
            setOpen(true);
          }}
          sx={{
            width: 'fit-content',
            height: '34px',
            borderRadius: '6px',
            textTransform: 'none',
            boxShadow: 'none',
            padding: '8px 16px',
          }}
        >
          Create Function
        </Button>
      </>
    );
  };

  const renderWebhookListSkeleton = () => {
    return (
      <Box
        width="100%"
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        {[...Array(3)].map((_, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                <Skeleton variant="text" width={150} height={24} />
                <Skeleton variant="text" width={250} height={20} />
              </Box>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Skeleton variant="circular" width={30} height={30} />
                <Skeleton variant="circular" width={30} height={30} />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        height="100%"
        gap="16px"
        width="100%"
      >
        {isLoading ? (
          renderWebhookListSkeleton()
        ) : webforms.length > 0 ? (
          renderWebhookList()
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            height="100%"
            gap="16px"
            width="100%"
            py="16px"
            justifyContent="center"
          >
            {renderEmptyState()}
          </Box>
        )}
        <WebFormFunctionDrawer
          open={open}
          onClose={() => setOpen(false)}
          isEditing={!!editingWebform}
          webformData={editingWebform}
        />

        {/* Add the DeleteModal */}
        <DeleteModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={handleConfirmDelete}
          isPending={isDeletePending}
          title="Delete Webform Function"
          description="Are you sure you want to delete this webform function? This action cannot be undone."
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
        />
        <MessagePopup alert={alert} setAlert={setAlert} />
      </Box>
    </>
  );
};

export default WebForm;
